// app/components/ResponsiveIK.tsx
import React from "react";

type Props = {
  /** path full setelah domain — atau path lengkap tanpa domain */
  src: string; // contoh: "https://ik.imagekit.io/didigi/.../file.webp"
  alt: string;

  /** ukuran tampilan CSS (px) utk breakpoint utama; PSI kamu untuk card ≈ 378x211 → pakai 400x225 */
  displayWidth: number; // contoh: 400
  displayHeight?: number; // contoh: 225

  /** lebar untuk srcset (1x & 2x, dst.) */
  widths?: number[]; // default [displayWidth, displayWidth*2]

  /** sizes untuk <img> */
  sizes?: string; // default: `${displayWidth}px`

  /** kelas Tailwind/React */
  className?: string;

  /** lazy untuk non-hero; hero = "eager" */
  loading?: "lazy" | "eager";

  /** PRIORITAS network untuk PSI (pakai lowercase attr `fetchpriority`) */
  priority?: "high" | "low" | "auto";

  /** kualitas (default 60) & format utama (default AVIF) kalau mau override */
  quality?: number;
  format?: "avif" | "webp";
};

function withParams(
  url: string,
  params: { w?: number | ""; h?: number | ""; q?: number; f?: "avif" | "webp" }
) {
  const u = new URL(url);
  const { w = "", h = "", q = 60, f = "avif" } = params;
  const tr = [
    `f-${f}`, // format (default AVIF)
    w !== "" ? `w-${w}` : "",
    h !== "" ? `h-${h}` : "",
    `q-${q}`, // kualitas
    "fo-auto", // focus/fit otomatis
  ]
    .filter(Boolean)
    .join(",");
  u.searchParams.set("tr", tr);
  return u.toString();
}

export default function ResponsiveIK({
  src,
  alt,
  displayWidth,
  displayHeight,
  widths,
  sizes,
  className,
  loading = "lazy",
  priority, // ← tambahkan ini saat manggil hero
  quality = 60,
  format = "avif",
}: Props) {
  const domain = src.startsWith("http")
    ? src
    : `https://ik.imagekit.io/${src.replace(/^\/+/, "")}`;

  const ws = widths ?? [displayWidth, displayWidth * 2];
  const _sizes = sizes ?? `${displayWidth}px`;

  // URL sumber untuk <img> (format utama)
  const mainSrc = withParams(domain, {
    w: displayWidth,
    h: displayHeight ?? "",
    q: quality,
    f: format,
  });

  // alternate format untuk <source> kedua
  const altFormat: "avif" | "webp" = format === "avif" ? "webp" : "avif";

  const srcSetMain = ws
    .map(
      (w) =>
        `${withParams(domain, {
          w,
          h: displayHeight
            ? Math.round((displayHeight / displayWidth) * w)
            : "",
          q: quality,
        })} ${w}w`
    )
    .join(", ")
    .replace(/f-avif|f-webp/g, `f-${format}`);

  const srcSetAlt = srcSetMain.replace(`f-${format}`, `f-${altFormat}`);

  return (
    <picture>
      {/* Source 1: format utama (mis. AVIF) */}
      <source type={`image/${format}`} srcSet={srcSetMain} sizes={_sizes} />
      {/* Source 2: fallback (mis. WEBP) */}
      <source type={`image/${altFormat}`} srcSet={srcSetAlt} sizes={_sizes} />
      {/* IMG fallback — di sinilah kita set fetchpriority untuk PSI */}
      <img
        src={mainSrc}
        alt={alt}
        width={displayWidth}
        height={displayHeight}
        loading={loading}
        decoding="async"
        className={className}
        fetchpriority={priority}
      />
    </picture>
  );
}
